import * as z from 'zod'

import { MORTGAGE_ACCOUNT_MODE_FIELD } from '../utils/const'
import {
  markList,
  optionalText,
  ownershipFields,
  requiredAmount,
  requiredDate,
  requiredText,
  tagList,
  withOwnership,
} from '../utils/schema'
import { WizardAccountMode, WizardValues } from '../utils/types'

export const realEstateWithFinancingSchema = withOwnership(z.object({
  ...ownershipFields(),
  assetName: requiredText('Name this property'),
  assetType: optionalText(),
  description: optionalText(),
  amount: requiredAmount('Enter the purchase price'),
  date: requiredDate('Enter the date of purchase'),
  currency: optionalText(),
  [MORTGAGE_ACCOUNT_MODE_FIELD]: z
    .enum([WizardAccountMode.EXISTING, WizardAccountMode.NEW])
    .default(WizardAccountMode.EXISTING),
  /*
   * The existing liability `accountName` names, when picked from the list —
   * see MortgageStep.vue. Required only in that mode; `withMortgageAccount`
   * below is what actually enforces it, the same way `withOwnership` enforces
   * `ownerAccountId` only when its own mode calls for one.
   */
  accountId: optionalText(),
  /*
   * Filled either way: picking an existing mortgage copies its name in
   * beside its id, and naming a new one is this field directly — so the
   * summary and create() see one value however it got there.
   */
  accountName: optionalText(),
  principal: requiredAmount('Enter the principal'),
  /*
   * The mortgage's own description — not the property's `description` above.
   * Written onto the liability transaction itself, not onto either asset;
   * see create.ts's own `postTransaction`/`createHolding` calls.
   */
  accountDescription: optionalText(),
  /* What the valuation step recorded, if anything. See markList. */
  prices: markList(),
  tags: tagList(),
})).superRefine((values: WizardValues, ctx: z.core.$RefinementCtx) => {
  if (values[MORTGAGE_ACCOUNT_MODE_FIELD] === WizardAccountMode.NEW) {
    if (!values.accountName?.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['accountName'],
        message: 'Name the lender',
      })
    }
    return
  }

  if (!values.accountId?.trim()) {
    ctx.addIssue({
      code: 'custom',
      path: ['accountId'],
      message: 'Pick the existing mortgage',
    })
  }
})

export type RealEstateWithFinancingValues = z.infer<typeof realEstateWithFinancingSchema>
